import { useEffect, type ComponentType } from 'react';
import { trackPageview } from './plausible';

/**
 * `computedMatch` and `location` are both injected by react-router v5's
 * `Switch` into the one child it renders. `computedMatch` is a v5 internal, so
 * it is absent from `RouteProps`.
 */
type SwitchedRouteProps = {
  computedMatch?: {
    path: string;
    params: Record<string, string | undefined>;
  };
  location?: { pathname: string; search: string };
};

/**
 * Wraps a react-router v5 `Route` so that each navigation reports a Plausible
 * pageview against the route's path pattern.
 *
 * Only routes rendered by a `Switch` report anything, because `Switch` is what
 * supplies the props above, and it renders a single matching child so one
 * navigation yields one pageview.
 */
export const withPlausibleRouting = <P extends object>(
  Route: ComponentType<P>
) => {
  const TrackedRoute = (props: P & SwitchedRouteProps) => {
    const { computedMatch, location } = props;

    const routePattern = computedMatch?.path;
    const params = computedMatch?.params;
    const pathname = location?.pathname;
    const search = location?.search ?? '';

    useEffect(() => {
      if (routePattern === undefined || pathname === undefined) return;
      trackPageview({ routePattern, params: params ?? {}, pathname, search });
      // `params` is a new object every render, and the pattern and path already
      // identify the pageview, so it is deliberately not a dependency.
      // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [routePattern, pathname, search]);

    return <Route {...props} />;
  };

  TrackedRoute.displayName = `withPlausibleRouting(${
    Route.displayName || Route.name || 'Route'
  })`;

  return TrackedRoute;
};
